import React, { useEffect, useState, useRef } from "react";
import {
	Box,
	Typography,
	Skeleton,
	Card,
	CardContent,
	Avatar,
	Button,
	CardActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptIcon from "@mui/icons-material/Receipt";
import { fetchCustomer } from "@/hooks/customer";
import { fetchContract } from "@/hooks/contract";
import { fetchInvoice } from "@/hooks/invoice";

interface SharedRecordCardProps {
	type: "CUSTOMER" | "CONTRACT" | "INVOICE";
	id: string;
	onLoad?: () => void;
}

export const SharedRecordCard: React.FC<SharedRecordCardProps> = ({
	type,
	id,
	onLoad,
}) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const [inView, setInView] = useState(false);
	const [data, setData] = useState<any>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setInView(true);
					observer.disconnect();
				}
			},
			{ threshold: 0.1 },
		);

		if (ref.current) {
			observer.observe(ref.current);
		}

		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		if (inView && !data && !loading && !error) {
			const fetchData = async () => {
				setLoading(true);
				try {
					if (type === "CUSTOMER") {
						const customer = await fetchCustomer(id);
						if (customer) setData(customer);
						else setError(true);
					} else if (type === "CONTRACT") {
						const contract = await fetchContract(id);
						setData(contract);
					} else if (type === "INVOICE") {
						const invoice = await fetchInvoice(id);
						setData(invoice);
					}
				} catch (err) {
					console.error(err);
					setError(true);
				} finally {
					setLoading(false);
					setTimeout(() => {
						if (onLoad) onLoad();
					}, 50);
				}
			};
			fetchData();
		}
	}, [inView, data, loading, error, id, type]);

	let icon = <PersonIcon />;
	let title = "Мэдээлэл";
	let primaryText = "";
	let secondaryText = "";
	let thirdText = "";
	let buttonText = "";

	const handleNavigate = () => {
		if (type === "CUSTOMER") navigate(`/customer/${id}/grid`);
		else if (type === "CONTRACT") navigate(`/contracts/view/${id}`);
		else if (type === "INVOICE") navigate(`/invoice/${id}`);
	};

	if (type === "CUSTOMER") {
		icon = <PersonIcon />;
		title = "Харилцагч";
		buttonText = "Харилцагч руу очих";
		if (data) {
			primaryText =
				data.type === "company"
					? data.name
					: `${data.information?.firstName} ${data.information?.lastName}`;
			if (primaryText === "undefined undefined")
				primaryText = data.registrationNumber;
			const contractUid = data?.contracts?.[0]?.uid || data?.Contracts?.[0]?.uid;
			secondaryText = contractUid ? `Гэрээ: ${contractUid}` : "";
			thirdText = `РД: ${data.information?.registrationNumber}`;
		}
	} else if (type === "CONTRACT") {
		icon = <DescriptionIcon />;
		title = "Гэрээ";
		buttonText = "Гэрээ рүү очих";
		if (data) {
			primaryText = data.label || data.uid;
			secondaryText = data?.uid ? `Гэрээ: ${data?.uid}` : "";
			thirdText = `Үлдэгдэл: ${data.balance || 0}₮`;
		}
	} else if (type === "INVOICE") {
		icon = <ReceiptIcon />;
		title = "Нэхэмжлэх";
		buttonText = "Нэхэмжлэх рүү очих";
		if (data) {
			primaryText = `Нэхэмжлэх №${data.uid || data.id}`;
			secondaryText = data?.contract?.uid ? `Гэрээ: ${data?.contract?.uid}` : "";
			thirdText = `Нийт дүн: ${data.total}₮`;
		}
	}

	return (
		<Card
			ref={ref}
			sx={{
				width: "100%",
				borderRadius: 3,
				mt: 0.5,
				backgroundColor:
					theme.palette.mode === "dark"
						? "rgba(255,255,255,0.05)"
						: "background.paper",
				boxShadow: theme.palette.mode === "dark" ? "none" : theme.shadows[2],
				border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : theme.palette.divider}`,
			}}
		>
			<CardContent sx={{ p: "12px !important" }}>
				<Box sx={{ display: "flex", alignItems: "center", mb: 1, gap: 1 }}>
					<Avatar
						sx={{
							width: 24,
							height: 24,
							backgroundColor:
								type === "CUSTOMER"
									? "rgba(25, 118, 210, 0.1)"
									: type === "CONTRACT"
										? "rgba(156, 39, 176, 0.1)"
										: "rgba(237, 108, 2, 0.1)",
							color:
								type === "CUSTOMER"
									? "#1976d2"
									: type === "CONTRACT"
										? "#9c27b0"
										: "#ed6c02",
						}}
					>
						{React.cloneElement(icon as React.ReactElement, {
							sx: { fontSize: 16 },
						})}
					</Avatar>
					<Typography
						variant="caption"
						color="text.secondary"
						fontWeight="bold"
					>
						{title}
					</Typography>
				</Box>

				{loading || (!data && !error) ? (
					<Box>
						<Skeleton variant="text" width="80%" height={24} />
						<Skeleton variant="text" width="50%" height={20} />
					</Box>
				) : error ? (
					<Typography variant="body2" color="error">
						Мэдээлэл олдсонгүй
					</Typography>
				) : (
					<Box>
						<Typography variant="body2" fontWeight="600" noWrap>
							{primaryText}
						</Typography>
						{secondaryText && (
							<Typography variant="caption" color="text.secondary" display="block" noWrap>
								{secondaryText}
							</Typography>
						)}
						{thirdText && (
							<Typography variant="caption" color="text.secondary" display="block" noWrap>
								{thirdText}
							</Typography>
						)}
					</Box>
				)}
			</CardContent>
			<CardActions sx={{ pt: 0, px: 1.5, pb: 1.5 }}>
				<Button
					size="small"
					variant="contained"
					fullWidth
					onClick={handleNavigate}
					disableElevation
					sx={{
						borderRadius: 2,
						textTransform: "none",
						fontWeight: "bold",
						color: theme.palette.mode === "dark" ? "#ffffff" : undefined,
						backgroundColor:
							theme.palette.mode === "dark"
								? "rgba(255,255,255,0.1)"
								: "primary.main",
						"&:hover": {
							backgroundColor:
								theme.palette.mode === "dark"
									? "rgba(255,255,255,0.2)"
									: "primary.dark",
						},
					}}
				>
					{buttonText}
				</Button>
			</CardActions>
		</Card>
	);
};
